import {
  Combobox,
  Input,
  InputBase,
  InputBaseProps,
  ScrollArea,
  useCombobox,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { useEffect, useState } from 'react'

import { Icons } from 'src/common/components'

import styles from './select-with-pagination.module.scss'

type SelectWithPagination = {
  options: { label: string; value: string }[]
  initialValue?: string
  showArrowIcon?: boolean
  showSearchIcon?: boolean
  onCloseDropDown?: () => void
  onOptionSelect: (value: string) => void
  handlePaginate: () => void
  handleSearch: (search: string) => void
} & InputBaseProps

const SelectWithPaginationComponent = ({
  options,
  initialValue,
  label,
  required,
  onOptionSelect,
  onCloseDropDown,
  handlePaginate,
  handleSearch,
  showArrowIcon = true,
  showSearchIcon = true,
}: SelectWithPagination) => {
  const combobox = useCombobox({
    onDropdownClose: () => {
      if (onCloseDropDown) {
        onCloseDropDown()
      }
      combobox.resetSelectedOption()
    },
  })
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 600)

  const [value, setValue] = useState<string | null>(initialValue ?? null)

  const handleSubmit = (val: string) => {
    const valueId = options.find(({ label }) => val === label)
    setValue(val)
    onOptionSelect(valueId!.value)
    combobox.closeDropdown()
  }

  useEffect(() => {
    handleSearch(debouncedSearch)
  }, [debouncedSearch])

  const selectOptions = options.map(({ value, label }) => (
    <Combobox.Option value={label} key={value}>
      {label}
    </Combobox.Option>
  ))

  return (
    <Combobox store={combobox} onOptionSubmit={handleSubmit} offset={0}>
      <Combobox.Target>
        <InputBase
          label={label}
          required={required}
          component="button"
          type="button"
          pointer
          leftSection={showSearchIcon ? <Icons.Search /> : <></>}
          rightSection={showArrowIcon ? <Icons.ArrowSelect /> : <></>}
          rightSectionPointerEvents="none"
          onClick={() => combobox.toggleDropdown()}
          classNames={{
            input: styles['input'],
            label: styles['label'],
            wrapper: styles['wrapper'],
            section: styles['section'],
          }}
        >
          {value || <Input.Placeholder>Select a Benefit</Input.Placeholder>}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={`Search ${label}`}
        />
        <Combobox.Options>
          <ScrollArea.Autosize
            type="scroll"
            mah={160}
            onBottomReached={() => handlePaginate()}
          >
            {selectOptions.length > 0 ? (
              selectOptions
            ) : (
              <Combobox.Empty>Nothing found</Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export default SelectWithPaginationComponent
